import { useCallback, useState } from "react";

// Operator-adjustable settings for how quickly captions reach listeners.
// Stored in this browser only (localStorage): they describe how THIS operator
// wants to run the room, and need no server round trip.

export type SpeedPreset = "fastest" | "balanced" | "quality";

/** Pause lengths (ms) offered for "a pause ends a phrase". */
export const PAUSE_CHOICES = [300, 500, 800, 1000] as const;

export interface LiveSettings {
  speed: SpeedPreset;
  /** Show listeners the dashed live line while the speaker is mid-phrase. */
  livePreview: boolean;
  /** Silence (ms) that ends a phrase; read when the microphone is started. */
  pauseMs: number;
}

export interface SpeedPresetInfo {
  label: string;
  hint: string;
  chunker: {
    maxWords: number;
    holdBackWords: number;
    maxMs: number;
    minWordsForTimedCommit: number;
    minChunkWords: number;
    clauseWords: number;
    pauseCommitMs: number;
    capSlackWords: number;
  };
}

// Like a human interpreter, the system sends each IDEA as soon as it is complete: at the end of a
// sentence, at a clause boundary ("…the first phase | and we are now…"), or when the speaker pauses.
// Only when the speaker runs on without any of those does the word/time cap cut the speech.
// "Cap" = the most words that wait before a cut is forced; "pause" = how long a hesitation counts as
// the end of an idea. Shorter = captions sooner but the translator sees less context.
export const SPEED_PRESETS: Record<SpeedPreset, SpeedPresetInfo> = {
  fastest: {
    label: "Fastest",
    hint: "Sends after a very short pause or about 4 words. Shortest delay, but the translator sees less context, so wording can be rougher.",
    chunker: { maxWords: 5, holdBackWords: 2, maxMs: 2000, minWordsForTimedCommit: 4, minChunkWords: 2, clauseWords: 3, pauseCommitMs: 250, capSlackWords: 2 },
  },
  balanced: {
    label: "Balanced",
    hint: "Like an interpreter: sends each idea at the end of a sentence, a natural clause break, or a short pause (recommended).",
    chunker: { maxWords: 7, holdBackWords: 2, maxMs: 3000, minWordsForTimedCommit: 5, minChunkWords: 3, clauseWords: 5, pauseCommitMs: 400, capSlackWords: 3 },
  },
  quality: {
    label: "Best quality",
    hint: "Waits for longer ideas (about 12 words or a clear pause), and gives a forced cut extra room to find a natural break first. Most natural translation, recommended for technical or continuous speech; captions arrive later.",
    chunker: { maxWords: 13, holdBackWords: 3, maxMs: 6000, minWordsForTimedCommit: 7, minChunkWords: 4, clauseWords: 8, pauseCommitMs: 600, capSlackWords: 6 },
  },
};

export const DEFAULT_SETTINGS: LiveSettings = { speed: "quality", livePreview: true, pauseMs: 500 };

const STORAGE_KEY = "afa_live_settings";

/** Turns anything read from storage into valid settings, falling back per field to the default. */
export function parseSettings(raw: unknown): LiveSettings {
  const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    speed: typeof obj.speed === "string" && obj.speed in SPEED_PRESETS ? (obj.speed as SpeedPreset) : DEFAULT_SETTINGS.speed,
    livePreview: typeof obj.livePreview === "boolean" ? obj.livePreview : DEFAULT_SETTINGS.livePreview,
    pauseMs: (PAUSE_CHOICES as readonly number[]).includes(obj.pauseMs as number)
      ? (obj.pauseMs as number)
      : DEFAULT_SETTINGS.pauseMs,
  };
}

export function loadSettings(): LiveSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return parseSettings(stored ? JSON.parse(stored) : null);
  } catch {
    return DEFAULT_SETTINGS; // storage blocked or corrupt: just use the defaults
  }
}

function saveSettings(settings: LiveSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* private mode / storage full: the settings still apply for this visit */
  }
}

export function useLiveSettings(): [LiveSettings, (patch: Partial<LiveSettings>) => void] {
  const [settings, setSettings] = useState<LiveSettings>(loadSettings);
  const update = useCallback((patch: Partial<LiveSettings>) => {
    setSettings((prev) => {
      const next = parseSettings({ ...prev, ...patch });
      saveSettings(next);
      return next;
    });
  }, []);
  return [settings, update];
}
